import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AppContext } from "../../../../common/context/Provider";
import {
  handlerSetTime,
  hanlderGetHours,
} from "../../../../utils/handlerDates";
import { BaseConfirmModal } from "../BaseConfirmModal";
import { Modal } from "../BaseModal";
import { CreateAdditionalHoursProps } from "./CreateAdditionalHours.Props";
import { AdditionalHourModel } from "../../../../models";
import { CreateAdditionalHoursModalView } from "./CreateAdditionalHoursModalView";
import {
  AdditionalHourTableRow,
  CreateAdditionalHoursModalLabels,
} from "./CreateAdditionalHoursModalView.Props";

const formatHourDisplay = (hour: string) => {
  if (!hour) {
    return "";
  }

  const slice = hour.slice(0, 5);
  const isPm = slice.split(":")[0] > "12";

  return slice + (isPm ? " PM" : " AM");
};

export const CreateAdditionalHoursModal = ({
  setModalOpen,
  saveAdditionalHours,
  isOpen,
  startDate,
  endDate,
  employee,
}: CreateAdditionalHoursProps) => {
  const { hourTypes, langResources, interruptionReferenceTypesList } =
    useContext(AppContext);

  const [startHour, setStartHour] = useState("");
  const [endHour, setEndHour] = useState("");
  const [referenceTypeId, setReferenceTypeId] = useState<number>(0);
  const [observations, setObservations] = useState("");
  const [isExtra, setIsExtra] = useState(false);
  const [hourTypeId, setHourTypeId] = useState<number | undefined>(undefined);
  const [hourTypeList, setHourTypeList] = useState(hourTypes);
  const [additionalHoursList, setAdditionalHoursList] = useState<
    AdditionalHourModel[]
  >([]);

  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const modalLabels = langResources["create.additional.time.Modal"];

  const labels: CreateAdditionalHoursModalLabels = {
    starDate: modalLabels["starDate"],
    endDate: modalLabels["endDate"],
    observationsLabel: modalLabels["observations.label"],
    selectHourTypePlaceholder: modalLabels["select.hour.type.placeholder"],
    extraLabel: modalLabels["extra.label"],
    addButton: modalLabels["add.button"],
    hourTypeLabel: modalLabels["hour.type.label"],
    observationsTableLabel: modalLabels["observations.label"],
    hoursLabel: modalLabels["hours.label"],
    startLabel: modalLabels["start.label"],
    endLabel: modalLabels["end.label"],
  };

  const getHourTypeName = (typeId: number | undefined) => {
    return hourTypes.find((hourType) => hourType.id === typeId)?.name;
  };

  const getInterruptionReferenceTypeName = (typeId: number | undefined) => {
    return interruptionReferenceTypesList.find(
      (hourType) => hourType.id === typeId,
    )?.name;
  };

  const handleChangeHourType = (e: ChangeEvent<HTMLInputElement>) => {
    let type = e.target.value;

    let result = hourTypes.find((hourType) =>
      hourType.abbreviature
        .toLocaleLowerCase()
        .startsWith(type.toLocaleLowerCase()),
    );

    if (result !== undefined) {
      setHourTypeId(result.id);
    }
  };

  const handlerAddAdditionalHour = () => {
    const newHour: AdditionalHourModel = {
      startHour,
      endHour,
      observations,
      hourType: hourTypeId,
      isOnlyOvertime: isExtra,
      interruptionReferenceId: referenceTypeId == 0 ? null : referenceTypeId,
    };

    if (startHour.trim() === "" || endHour.trim() === "") {
      toast.error(modalLabels["validation.all.fields.required"], {
        autoClose: 5000,
        position: toast.POSITION.BOTTOM_RIGHT,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    if (
      startDate + "T" + newHour.startHour >=
      startDate + "T" + newHour.endHour
    ) {
      toast.error(langResources["msg.validation.startDate"], {
        autoClose: 5000,
        position: toast.POSITION.BOTTOM_RIGHT,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setAdditionalHoursList((prev) => [...prev, newHour]);

    setStartHour("");
    setEndHour("");
    setObservations("");
    // @ts-ignore
    document.getElementById("hourType").value = "";
    setHourTypeList(hourTypes);
    setIsExtra(false);
    setHourTypeId(undefined);
    setReferenceTypeId(0);
  };

  const handlerCansel = () => {
    if (additionalHoursList.length > 0) {
      setIsOpenConfirmationModal(true);
    } else {
      setAdditionalHoursList([]);
      setModalOpen(false);
    }
  };

  const handleConfirmCloseModal = () => {
    setIsOpenConfirmationModal(true);
    setAdditionalHoursList([]);
    setModalOpen(false);
  };

  const handlerSubmit = () => {
    if (additionalHoursList.length > 0) {
      setModalOpen(false);
      setAdditionalHoursList([]);
      saveAdditionalHours(additionalHoursList, employee, startDate, endDate);
    }
  };

  const handleDeleteAdditionalHour = (index: number) => {
    let newListOfAdditionals = additionalHoursList.filter(
      (_, i) => i !== index,
    );

    setAdditionalHoursList(newListOfAdditionals);
  };

  useEffect(() => {
    if (isExtra) {
      setHourTypeList(hourTypeList.filter((type) => type.isExtraHour == true));
    } else {
      setHourTypeList(hourTypes);
    }
  }, [isExtra]);

  const onChangeInterruptionTypesReference = (
    name: string,
    value: string | null | number | undefined,
  ) => {
    let result = interruptionReferenceTypesList?.find(
      (interruption) => interruption.id == value,
    );

    if (result) {
      setReferenceTypeId(result.id);
    }
  };

  const tableRows: AdditionalHourTableRow[] = useMemo(
    () =>
      additionalHoursList.map((hour, index) => ({
        id: index,
        hourTypeLabel:
          hour.hourType == undefined
            ? "LIQ. Automatica"
            : (getHourTypeName(hour.hourType) ?? ""),
        observations: hour.observations,
        hours: hanlderGetHours(
          startDate,
          hour.startHour.split(" ")[0],
          hour.endHour.split(" ")[0],
        ),
        startDisplay: formatHourDisplay(hour.startHour),
        endDisplay: formatHourDisplay(hour.endHour),
        referenceLabel:
          (hour.interruptionReferenceId &&
            getInterruptionReferenceTypeName(hour.interruptionReferenceId)) ||
          "",
      })),
    [additionalHoursList, startDate, hourTypes, interruptionReferenceTypesList],
  );

  const totalRegisteredHours = useMemo(() => {
    const total = additionalHoursList.reduce((sum, hour) => {
      const hours = hanlderGetHours(
        startDate,
        hour.startHour.split(" ")[0],
        hour.endHour.split(" ")[0],
      );
      return sum + Number(hours);
    }, 0);

    return total.toFixed(2);
  }, [additionalHoursList, startDate]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        modalTitle={modalLabels["title"]}
        onClose={handlerCansel}
        onSubmit={handlerSubmit}
        disableSubmit={additionalHoursList.length === 0}
      >
        <CreateAdditionalHoursModalView
          labels={labels}
          startHour={startHour}
          endHour={endHour}
          observations={observations}
          referenceTypeId={referenceTypeId}
          isExtra={isExtra}
          hourTypeList={hourTypeList}
          tableRows={tableRows}
          totalRegisteredHours={totalRegisteredHours}
          onStartHourChange={(dates) => handlerSetTime(dates, setStartHour)}
          onEndHourChange={(dates) => handlerSetTime(dates, setEndHour)}
          onObservationsChange={setObservations}
          onReferenceChange={onChangeInterruptionTypesReference}
          onHourTypeChange={handleChangeHourType}
          onIsExtraChange={setIsExtra}
          onAddHour={handlerAddAdditionalHour}
          onDeleteHour={handleDeleteAdditionalHour}
        />
      </Modal>

      {isOpenConfirmationModal && (
        <BaseConfirmModal
          isLoading={false}
          isOpen={isOpenConfirmationModal}
          isShowControls={true}
          modalTitle={""}
          onClose={() => setIsOpenConfirmationModal(false)}
          onSubmit={handleConfirmCloseModal}
        >
          <div className="modal-body pt-1 text-center">
            <h4>{modalLabels["modal.confirmation.label"]}</h4>
          </div>
        </BaseConfirmModal>
      )}
    </>
  );
};
